import React, { useEffect, useState } from "react";
import { Dialog, Classes } from "@blueprintjs/core";
import { useTranslation } from "react-i18next";
import { Icon } from "@blueprintjs/core";
import moment from "moment";
import {createSelector} from "reselect";

// Components
import Button from "../common/components/button";
import TextInput from "../common/components/text-input";
import DateInput from "../common/components/date-input";

// Selectors
import { documentationSelector } from "./documentation-selectors";

// Actions 
import { deleteDocumentation, downloadDocumentation, createDocumentation, updateDocumentation, readDocumentation } from "./documentation-actions";
import { useDispatch, useSelector } from "react-redux";

// Hooks
import useInputValidation from "../common/hooks/use-input-validation";

const documentationPageSelector = createSelector(
    documentationSelector,
    (documentation) => {
        documentation = documentation && documentation.toJS();
        documentation = (Array.isArray(documentation) && documentation) || [];
        return {
            documentation: documentation,
        }
    }
);

// Redux dispatch hook
const useReduxDispatch = () => {
    const dispatch = useDispatch();
    return {
        readDocumentation: (client) => dispatch(readDocumentation(client)),
        deleteDocumentation: (documentation) => dispatch(deleteDocumentation(documentation)),
        downloadDocumentation: (documentation, fileName) => dispatch(downloadDocumentation(documentation, fileName)),
        createDocumentation: (data) => dispatch(createDocumentation(data)),
        updateDocumentation: (fileId, data) => dispatch(updateDocumentation(fileId, data))
    }
};

const DocumentationClientDialog = (props) => {
    const {isOpen, onClose, client} = props;

    // i18n
    const {t} = useTranslation("clients");

    const { documentation } = useSelector(state => documentationPageSelector(state));

    // Redux
    const {deleteDocumentation, downloadDocumentation, createDocumentation, updateDocumentation, readDocumentation} = useReduxDispatch();

    // States
    const [nameData, setName] = useInputValidation();
    const [nameUpdateFile, setNameUpdateFile] = useInputValidation();
    const [nameFile, setNameFile] = useInputValidation();
    const [nameOriginalFile, setNameOriginalFile] = useInputValidation();
    const [nameDeleteFile, setNameDeleteFile] = useInputValidation();

    const [descriptionFile, setDescriptionFile] = useInputValidation();
    const [descriptionUpdateFile, setDescriptionUpdateFile] = useInputValidation();
    const [descriptionDeleteFile, setdescriptionDeleteFile] = useInputValidation();

    const [sizeFile, setSizeFile] = useInputValidation();
    const [extensionFile, setExtensionFile] = useInputValidation();
    const [fileId, setFileId] = useState("");
    
    const [startDate, setStartDate] = useInputValidation();
    const [startUpdateDate, setStartUpdateDate] = useInputValidation();
    const [startDateDeleteFile, setStartDateDeleteFile] = useInputValidation();

    const [endDate, setEndDate] = useInputValidation();
    const [endUpdateDate, setEndUpdateDate] = useInputValidation();
    const [endDateDeleteFile, setEndDateDeleteFile] = useInputValidation();

    const [selectedFile, setSelectedFile] = useInputValidation()
    const [selectedUpdateFile, setSelectedUpdateFile] = useInputValidation()

    const [openAddSection, setOpenAddSection] = useState(false);
    const [openEditSection, setOpenEditSection] = useState(false);
    const [openDeleteSection, setOpenDeleteSection] = useState(false);
    
    const formDataFile = new FormData();
    const formUpdateDataFile = new FormData();

    // Set form state when client data changes
    useEffect(() => setFormData(client), [client]);

    useEffect(() => {
        if(isOpen){
            readDocumentation({client: client._id});
        }
    }, [isOpen]);
        

    const setFormData = client => {
        setName(client.name);
    };

    const resetValues = () => {
        if(openAddSection){
            setOpenAddSection(false);
            setSelectedFile("");
            setNameFile("");
            setStartDate("");
            setEndDate("");
            setDescriptionFile("")
        } else if(openEditSection){
            setOpenEditSection(false);
            setSelectedUpdateFile("");
            setNameUpdateFile("");
            setStartUpdateDate("");
            setEndUpdateDate("");
            setDescriptionUpdateFile("")
        }else if(openDeleteSection) {
            setOpenDeleteSection(false)
        } else {
            onClose();
        }
    }

    const deleteFile = (documentationId) => {
        deleteDocumentation(documentationId);
        resetValues()
        onClose()
    }

    const downloadFile = (documentationId, fileName) => {
        downloadDocumentation(documentationId, fileName)
    }

    const createFile = (fileName, clientId, startDate, endDate, selectedFile, description) => {
        formDataFile.append('clientId', clientId);
        formDataFile.append('fileName', fileName);
        formDataFile.append('startDate', startDate);
        formDataFile.append('endDate', endDate);
        formDataFile.append('file', selectedFile);
        formDataFile.append('description', description);
        createDocumentation(formDataFile);
        resetValues()
        onClose()
    }
    
    const openEditDialog = (fileName, name, startDate, endDate, fileId, extension, size, description) => {
        if(openAddSection){
            setOpenAddSection(false);
        } 
        if(openDeleteSection){
            setOpenDeleteSection(false);
        } 
        setOpenEditSection(true); 
        setFileId(fileId);
        setNameUpdateFile(name);
        setNameOriginalFile(fileName);
        setStartUpdateDate(startDate);
        setEndUpdateDate(endDate);
        setDescriptionUpdateFile(description)
        setExtensionFile(extension);
        setSizeFile(size);
    }

    const editSection = (fileName, startDate, endDate, selectedUpdateFile, descriptionUpdate) => {
        formUpdateDataFile.append('clientId', client._id);
        formUpdateDataFile.append('fileName', fileName);
        formUpdateDataFile.append('startDate', startDate);
        formUpdateDataFile.append('endDate', endDate);
        formUpdateDataFile.append('file', selectedUpdateFile);
        formUpdateDataFile.append('description', descriptionUpdate);
        updateDocumentation(fileId, formUpdateDataFile);
        resetValues()
        onClose()
    }

    const deleteSection = (fileId, fileName, name, startDate, endDate, extension, size, description) => {
        const startFormatDate = moment(startDate).format("DD/MM/YYYY")
        const endFormatDate = moment(endDate).format("DD/MM/YYYY")
        if(openAddSection){
            setOpenAddSection(false);
        } 
        if(openEditSection){
            setOpenEditSection(false);
        } 
        setFileId(fileId);
        setNameOriginalFile(fileName);
        setNameDeleteFile(name);
        setStartDateDeleteFile(startFormatDate);
        setEndDateDeleteFile(endFormatDate);
        setOpenDeleteSection(true);
        setdescriptionDeleteFile(description)
        setExtensionFile(extension);
        setSizeFile(size);
    }

    const styles = {
        uploadDoc: {
            display: 'flex',
            alignItems: 'center',
            marginBottom: "15px",
            button: {
                backgroundColor: "#1473B7",
                label: {
                    display: 'flex',
                    width: "150px",
                    justifyContent: 'center',
                    alignItems: 'center',
                    margin: 0,
                    cursor: 'pointer',
                    color: "white"
                }
            }
        }
    }

    return (
        <Dialog
            isOpen={isOpen}
            onClose={onClose}
            lazy={true}
            style={{
                backgroundColor: "white",
                paddingBottom: "40px",
                width: "650px"
            }}
        >
            <div
                style={{margin: "20px 40px"}}
                className={Classes.DIALOG_BODY}
            >
                <h3 style={{margin: "20px 0", width: "350px"}}>{t("documentationClientDialog.title")}</h3>
                <span style={{margin: "20px 0", fontSize: "20px", color:"#43C0C8"}}>{nameData.value}</span>
                <div style={{margin: "10px 0", display: "flex", flexDirection:"row"}} >
                    <span>{t("documentationClientDialog.sap")}</span>
                    <span style={{fontWeight: "bold"}}>{client.sap_id}</span>
                </div>
                <div style={{margin: "10px 0", display: "flex", flexDirection:"row"}} >
                    <span>{t("documentationClientDialog.nif")}</span>
                    <span style={{fontWeight: "bold"}}>{client.nif}</span>
                </div>
                <div style={{display: "flex", flexDirection:"row", alignItems: "center"}}>
                    <span>{documentation.length + " " + t("documentationClientDialog.availableDocumentation")}</span>
                    <Button
                        text={t("documentationClientDialog.input.add")}
                        style={{marginLeft: "20px", width: "20%"}}
                        action={() => {
                            if(openEditSection){
                                setOpenEditSection(false);
                                setOpenAddSection(!openAddSection);
                            } else if(openDeleteSection){
                                setOpenDeleteSection(false);
                                setOpenAddSection(!openAddSection);
                            } else {
                                setOpenAddSection(true)
                            }
                        }}
                        intent={"secondary"}
                    />
                </div>
                {documentation.length > 0 && 
                    <div style={ documentation.length > 3 ? {maxHeight: 200, overflowY: "scroll"} : {}}>
                        <table className="table" style={{marginTop: "20px"}}>
                            <tbody>
                                <tr style={{borderBottom: "1px solid #C6C8C9"}}>
                                    <th><span>{t("documentationClientDialog.table.documentName")}</span></th>
                                    <th><span>{t("documentationClientDialog.table.type")}</span></th>
                                    <th><span>{t("documentationClientDialog.table.size")}</span></th>
                                </tr>
                                {documentation.map(doc => {
                                    return (
                                        <tr key={doc._id}>
                                            <td style={{ width: "300px" }}><span>{doc.name}</span></td>
                                            <td>{doc.extension}</td>
                                            <td><span>{(Math.trunc(doc.size/1024))} KB</span></td>
                                            <td style={{textAlign:"right"}}>
                                                <div>
                                                    <Icon style={{ cursor: "pointer" }} icon="download" iconSize={20} onClick={() => downloadFile(doc._id, doc.fileName)} />
                                                    <Icon style={{ cursor: "pointer", marginLeft: "10px" }} icon="edit" iconSize={20} onClick={() => openEditDialog(doc.fileName, doc.name, doc.startDate, doc.endDate, doc._id, doc.extension, doc.size, doc.description)} />
                                                    <Icon style={{ cursor: "pointer", marginLeft: "10px" }} icon="trash" iconSize={20} onClick={() => deleteSection(doc._id, doc.fileName, doc.name, doc.startDate, doc.endDate, doc.extension, doc.size, doc.description)} />
                                                </div>
                                            </td>
                                        </tr>                               
                                    )})}
                            </tbody>
                        </table>
                    </div>
                }
                {openAddSection && 
                    <div style={{marginTop: "30px"}}>
                        <span style={{fontSize: "20px"}}>{t("documentationClientDialog.input.addDocument")}</span>
                        <div style={{marginTop: "15px"}}>
                            <div style={{margin: "10px 0", display: "flex", flexDirection:"row", alignItems: "center"}} >
                                <div
                                    style={ styles.uploadDoc.button }
                                    className="btn"
                                >
                                    <label
                                        style={ styles.uploadDoc.button.label }
                                    >
                                        <TextInput
                                            style={{display: "none"}}
                                            type="file"
                                            value={selectedFile.value.file}
                                            fileValue={true}
                                            setValue={setSelectedFile}
                                            validation
                                            isValid={selectedFile.isValid}
                                            errorMessage={selectedFile.errorMessage}
                                        />
                                        {t("documentationClientDialog.input.label.uploadFile")}
                                    </label>
                                </div>
                                <label style={{marginLeft: "20px", fontWeight: "bold", maxWidth: "270px"}}>
                                        {selectedFile?.value[0]?.name}
                                </label>
                                <label style={{marginLeft: "10px"}}>
                                        { selectedFile?.value[0]?.size && ("(" + selectedFile?.value[0]?.name.substr(selectedFile?.value[0]?.name.lastIndexOf('.')).replace(/\./g, '').toUpperCase() + ", " + Math.trunc(selectedFile?.value[0]?.size/1024) + " KB)")}
                                </label>
                            </div>
                            <div style={{ display: "flex", flexDirection:"row", marginTop: "15px" }}>
                                <TextInput
                                    label={t("documentationClientDialog.input.label.fileName") + " *"}
                                    style={{width: "60%", marginRight: "20px"}}
                                    value={nameFile.value}
                                    setValue={setNameFile}
                                    validation
                                    isValid={nameFile.isValid}
                                    errorMessage={nameFile.errorMessage}
                                />
                                <DateInput
                                    labelText={t("documentationClientDialog.input.label.startDate") + " *"}
                                    inputWrapperStyle={{marginRight: 15}}
                                    value={startDate.value}
                                    validation={true}
                                    onChange={newDate => setStartDate(newDate?.startOf("day") || newDate)}
                                    errorMessage={startDate.errorMessage}
                                />
                                <DateInput
                                    labelText={t("documentationClientDialog.input.label.endDate") + " *"}
                                    inputWrapperStyle={{marginRight: 15}}
                                    value={endDate.value}
                                    validation={true}
                                    onChange={newDate => setEndDate(newDate?.startOf("day") || newDate)}
                                    errorMessage={endDate.errorMessage}
                                />
                            </div>
                            <TextInput
                                label={t("documentationClientDialog.input.label.description")}
                                style={{marginTop: "15px"}}
                                value={descriptionFile.value}
                                setValue={setDescriptionFile}
                                validation
                                errorMessage={descriptionFile.errorMessage}
                            />
                        </div>
                    </div>
                }  
                {openEditSection && 
                    <div style={{marginTop: "30px"}}>
                        <span style={{fontSize: "20px"}}>{t("documentationClientDialog.input.editDocument")}</span>
                        <div style={{marginTop: "15px"}}>
                            <div style={{margin: "10px 0", display: "flex", flexDirection:"row", alignItems: "center"}} >
                                <div
                                    style={ styles.uploadDoc.button }
                                    className="btn"
                                >
                                    <label
                                        style={ styles.uploadDoc.button.label }
                                    >
                                        <TextInput
                                            style={{ marginTop: "15px", display: "none" }}
                                            type="file"
                                            value={selectedUpdateFile.value.file}
                                            fileValue={true}
                                            setValue={setSelectedUpdateFile}
                                            errorMessage={selectedUpdateFile.errorMessage}
                                        />
                                        {t("documentationClientDialog.input.label.uploadFile")}
                                    </label>
                                </div>
                                <label style={{marginLeft: "20px", fontWeight: "bold", maxWidth: "270px"}}>
                                        {selectedUpdateFile?.value[0]?.name ? 
                                            selectedUpdateFile?.value[0]?.name : nameOriginalFile.value}
                                </label>
                                <label style={{marginLeft: "10px"}}>
                                        { selectedUpdateFile?.value[0]?.size ? 
                                            ( "(" + selectedUpdateFile?.value[0]?.name.substr(selectedUpdateFile?.value[0]?.name.lastIndexOf('.')).replace(/\./g, '').toUpperCase() + ", " + Math.trunc(selectedUpdateFile?.value[0]?.size/1024) + " KB)") : 
                                            ( "(" + extensionFile.value + ", " + (Math.trunc(sizeFile.value/1024) + " KB)"))}
                                </label>
                            </div>
                            <div style={{ display: "flex", flexDirection:"row", marginTop: "15px"}} >
                                <TextInput
                                    label={t("documentationClientDialog.input.label.fileName")}
                                    style={{width: "60%", marginRight: "20px"}}
                                    value={nameUpdateFile.value}
                                    setValue={setNameUpdateFile}
                                    errorMessage={nameUpdateFile.errorMessage}
                                />
                                <DateInput
                                    labelText={t("documentationClientDialog.input.label.startDate")}
                                    inputWrapperStyle={{marginRight: 15}}
                                    value={moment(startUpdateDate.value)}
                                    onChange={newDate => setStartUpdateDate(newDate?.startOf("day") || newDate)}
                                />
                                <DateInput
                                    labelText={t("documentationClientDialog.input.label.endDate")}
                                    inputWrapperStyle={{marginRight: 15}}
                                    value={moment(endUpdateDate.value)}
                                    onChange={newDate => setEndUpdateDate(newDate?.startOf("day") || newDate)}
                                />
                            </div>
                            <TextInput
                                label={t("documentationClientDialog.input.label.description") + " *"}
                                style={{marginTop: "15px"}}
                                value={descriptionUpdateFile.value}
                                setValue={setDescriptionUpdateFile}
                                validation
                                errorMessage={descriptionUpdateFile.errorMessage}
                            />
                        </div>
                    </div>
                }  
                {openDeleteSection && 
                    <div style={{margin: "30px 0", display: "flex", flexDirection: "column"}} >
                        <span style={{fontSize: "20px", marginTop: "10px"}}>{t("documentationClientDialog.input.delete")}</span>
                        <span style={{marginTop: "20px"}}>{t("documentationClientDialog.input.deleteText")}</span>
                        <span style={{fontWeight: "bold", marginTop: "20px", marginLeft: "25px"}}>{nameDeleteFile.value}</span>
                        <span style={{marginTop: "10px", width:"400px", marginLeft: "25px"}}>{descriptionDeleteFile.value}</span>
                        {/* <span style={{marginTop: "20px", marginLeft: "25px"}}>{nameOriginalFile.value  + " " + ( "(" + extensionFile.value.replace(/\./g, '').toUpperCase() + ", " + (Math.trunc(sizeFile.value/1024) + " KB)"))}</span> */}
                        <span style={{marginTop: "20px", marginLeft: "25px"}}>{nameOriginalFile.value  + " " + ( "(" + extensionFile.value + ", " + (Math.trunc(sizeFile.value/1024) + " KB)"))}</span>
                        <span style={{marginTop: "20px", marginLeft: "25px"}}>{startDateDeleteFile.value}</span>
                        <span style={{marginTop: "20px", marginLeft: "25px"}}>{endDateDeleteFile.value}</span>
                    </div>
                }
            </div>
            <div style={{margin: "20px 40px 0 40px"}} className={Classes.DIALOG_FOOTER}>
                <div className={Classes.DIALOG_FOOTER_ACTIONS}>
                    <Button
                        text={(openAddSection || openEditSection || openDeleteSection) ? 
                                t("documentationClientDialog.input.cancel") : 
                                t("documentationClientDialog.input.close")}
                        action={() => {
                            resetValues()
                        }}
                        intent={"cancel"}
                        style={(openAddSection || openEditSection || openDeleteSection) ? {marginRight: "15px"} : {margin: 0}}
                    />
                    {(openAddSection || openEditSection || openDeleteSection) &&
                        <Button
                            text={openAddSection ? t("documentationClientDialog.input.upload") : openEditSection ? t("documentationClientDialog.input.save") : t("documentationClientDialog.input.delete")}
                            action={() => {
                                openAddSection ? createFile(nameFile.value, client._id, startDate.value, endDate.value, selectedFile.value[0], descriptionFile.value) : 
                                openEditSection ? editSection(nameUpdateFile.value, startUpdateDate.value, endUpdateDate.value, selectedUpdateFile.value[0], descriptionUpdateFile.value) : 
                                deleteFile(fileId)
                            }}
                            intent={openDeleteSection ? "red" : "secondary"}
                            disabled={openAddSection ? (!(!nameFile.isValid || !startDate.isValid || !endDate.isValid || !selectedFile.isValid)) : true}
                        />
                    }
                </div>
            </div>
        </Dialog>
    );
};

export default DocumentationClientDialog;